import React, { useEffect, useRef } from 'react'

import {
    SpinButtonSectionStore,
    useBetSliderStore,
    useConfigurationStore,
    useLuckySpinsStore,
    useMenuStore,
    useNavigationStore,
    usePlayerInfoStore,
    useSpinButtonSectionStore,
} from '@stores'

import NavButton from '@components/Navigation/NavButton'

import fullscreenDisabled from '@assets/button-fullscreen-enabled.svg'
import fullscreenEnabled from '@assets/button-fullscreenexit-enabled.svg'
import musicIconDisabled from '@assets/button-musicoff-enabled.svg'
import volumeIconDisabled from '@assets/button-volumeoff-enabled.svg'
import menuIconClosed from '@assets/close-button.svg'
import homeIcon from '@assets/home-button.svg'
import menuIcon from '@assets/menu-button.svg'
import musicIcon from '@assets/music-button.svg'
import volumeIcon from '@assets/volume-button.svg'
import { useOpacityToggle } from '@hooks'
import {
    BackgroundMusicClickedMessage,
    BaseGameType,
    BonusTypes,
    ChangeOpacityComponents,
    ChangeOpacityMessagePrefix,
    EndTurboMessage,
    FullscreenChangedMessage,
    FullscreenClickedMessage,
    GameUIMessageType,
    HasBackgroundMusicMessage,
    HomeButtonClickedMessage,
    MenuClickedMessage,
    // MenuClickedMessage,
    MusicButtonToggledMessage,
    SendHideStateMessage,
    SendStopStateMessage,
    SendStopStateWithWinMessage,
    SetIsHomeButtonDisabledMessage,
    SetIsMenuButtonDisabledMessage,
    SetIsMenuOpenMessage,
    SoundToggledMessage,
    SpinButtonSendBeforeGambleStateMessage,
    SpinButtonSendIdleMessage,
    SpinButtonSendIdleWithoutGambleStateMessage,
    StartTurboMessage,
    ViewportType,
} from 'game-ui-types'
import { BET_STEPPER_CONFIGS } from '@configs/gameConfigurations'
import { Menu } from '../'
import { useSoundStore } from '@stores/soundStore'
import { SOUNDS } from '@constants/sounds'
import { cn, hasFlag, isStandalone } from '@amusnet/react-ui'

const Navigation: React.FC = () => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const isSoundMuted = useConfigurationStore((state) => state.isSoundMuted)
    const isBackgroundMusicMuted = useConfigurationStore((state) => state.isBackgroundMusicMuted)
    const isFullscreen = useConfigurationStore((state) => state.isFullscreen)
    const setIsSoundMuted = useConfigurationStore((state) => state.setIsSoundMuted)
    const setIsBackgroundMusicMuted = useConfigurationStore((state) => state.setIsBackgroundMusicMuted)
    const setIsFullscreen = useConfigurationStore((state) => state.setIsFullscreen)
    const gameType = useConfigurationStore((state) => state.gameType)
    const shouldUseExternalMenu = useConfigurationStore((state) => state.shouldUseExternalMenu)
    const isExpoTournamentModeHidden = useConfigurationStore((state) => state.isExpoTournamentModeHidden)
    const shouldHideFullScreenButton = useConfigurationStore((state) => state.shouldHideFullScreenButton)

    const isMenuOpen = useNavigationStore((state) => state.isMenuOpen)
    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const setShouldOpenMenuDefaultSection = useMenuStore((state) => state.setShouldOpenDefaultSection)
    const setTargetMenuSectionId = useMenuStore((state) => state.setTargetMenuSectionId)

    const isScrolling = useBetSliderStore((state) => state.isScrolling)
    const betGroup = useBetSliderStore((state) => state.betGroup)

    const isMenuButtonDisabled = useNavigationStore((state) => state.isMenuDisabled)
    const setIsMenuButtonDisabled = useNavigationStore((state) => state.setIsMenuDisabled)
    const isHomeButtonDisabled = useNavigationStore((state) => state.isHomeDisabled)
    const setIsHomeButtonDisabled = useNavigationStore((state) => state.setIsHomeDisabled)
    const hasBackgroundMusic = useNavigationStore((state) => state.hasBackgroundMusic)
    const setHasBackgroundMusic = useNavigationStore((state) => state.setHasBackgroundMusic)
    const isMusicButtonDisabled = useNavigationStore((state) => state.isMusicButtonDisabled)
    const setIsMusicButtonDisabled = useNavigationStore((state) => state.setIsMusicButtonDisabled)

    const playEngineSound = useSoundStore((state) => state.playEngineSound)

    // navigation left wrapper config
    const navigationLeftWrapperConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('NavigationLeftWrapper').styles
    )

    // navigation right wrapper config
    const navigationRightWrapperConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('NavigationRightWrapper').styles
    )

    // navigation config
    const navigationConfigStyles = useConfigurationStore((state) => state.getComponentConfig('Navigation').styles)
    const navigationConfigPosition = useConfigurationStore((state) => state.getComponentConfig('Navigation').position)
    const navigationComponent = useRef<HTMLDivElement | null>(null)

    useOpacityToggle(ChangeOpacityComponents.NAVIGATION, navigationComponent)

    const isLongPress = useSpinButtonSectionStore((state) => state.isLongPress)
    const spinButtonState = useSpinButtonSectionStore((state: SpinButtonSectionStore) => state.state)

    const isBetInProgress = useSpinButtonSectionStore((state) => state.isBetInProgress)

    const isLongPressRef = useRef(isLongPress)
    const spinButtonStateRef = useRef(spinButtonState)
    const isBetInProgressRef = useRef(isBetInProgress)
    const isScrollingRef = useRef(isScrolling)

    const shouldHideHomeButton = hasFlag('shouldHideHomeButton')

    if (isExpoTournamentModeHidden) return null

    const updateHomeButtonState = (isDisabled: boolean) => {
        const isInLS = useLuckySpinsStore.getState().activeBonus?.type === BonusTypes.LUCKY_SPIN

        setIsHomeButtonDisabled(isInLS ? true : isDisabled)
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type, payload } = rawData

            if (
                type === `${ChangeOpacityMessagePrefix}${ChangeOpacityComponents.HOME_BUTTON}` &&
                window.parent !== window
            ) {
                window.parent.postMessage({ type: GameUIMessageType.CHANGE_OPACITY_HOME_BUTTON, payload }, '*')
            }

            switch (type) {
                case GameUIMessageType.HAS_BACKGROUND_MUSIC: {
                    const data = rawData as HasBackgroundMusicMessage

                    setHasBackgroundMusic(data.hasBackgroundMusic)
                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_HIDE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE_WITH_WIN:
                case GameUIMessageType.START_TURBO: {
                    const data = rawData as
                        | SendHideStateMessage
                        | SendStopStateMessage
                        | SendStopStateWithWinMessage
                        | StartTurboMessage

                    setIsMenuButtonDisabled(data.isMenuButtonDisabled ?? true)
                    updateHomeButtonState(data.isHomeButtonDisabled ?? true)
                    break
                }

                case GameUIMessageType.SPIN_BUTTON_SEND_GAMBLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_WAITING_FOR_FREESPIN_ANIMATION_START_STATE: {
                    setIsMenuButtonDisabled(true)
                    setIsHomeButtonDisabled(true)
                    setIsMusicButtonDisabled(true)
                    break
                }

                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_WITHOUT_GAMBLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_BEFORE_GAMBLE_STATE:
                case GameUIMessageType.END_TURBO: {
                    const data = rawData as
                        | SpinButtonSendIdleMessage
                        | SpinButtonSendIdleWithoutGambleStateMessage
                        | SpinButtonSendBeforeGambleStateMessage
                        | EndTurboMessage

                    setIsMenuButtonDisabled(data.isMenuButtonDisabled ?? false)
                    updateHomeButtonState(data.isHomeButtonDisabled ?? false)
                    setIsMusicButtonDisabled(isMusicButtonDisabled ?? true)
                    break
                }
                case GameUIMessageType.FULLSCREEN_CHANGED: {
                    const data = rawData as FullscreenChangedMessage

                    setIsFullscreen(data.isFullscreen)
                    break
                }
                case GameUIMessageType.SET_IS_MENU_OPEN: {
                    const data = rawData as SetIsMenuOpenMessage

                    setShouldOpenMenuDefaultSection(false)
                    setIsMenuOpen(data.isMenuOpen)
                    break
                }
                case GameUIMessageType.SET_MENU_SECTION_ID: {
                    const data = rawData

                    setTargetMenuSectionId(data.payload.id)

                    break
                }
                case GameUIMessageType.SET_IS_HOME_BUTTON_DISABLED: {
                    const data = rawData as SetIsHomeButtonDisabledMessage

                    updateHomeButtonState(data.isDisabled)
                    break
                }
                case GameUIMessageType.SET_IS_MENU_BUTTON_DISABLED: {
                    const data = rawData as SetIsMenuButtonDisabledMessage

                    setIsMenuButtonDisabled(data.isDisabled)
                    break
                }
                case GameUIMessageType.MUSIC_BUTTON_TOGGLED: {
                    const data = rawData as MusicButtonToggledMessage

                    setIsBackgroundMusicMuted(data.isMuted)
                    break
                }
                case GameUIMessageType.SOUND_TOGGLED: {
                    const data = rawData as SoundToggledMessage

                    setIsSoundMuted(data.isMuted, false)
                    break
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    useEffect(() => {
        isLongPressRef.current = isLongPress
    }, [isLongPress])

    useEffect(() => {
        isScrollingRef.current = isScrolling
    }, [isScrolling])

    useEffect(() => {
        isBetInProgressRef.current = isBetInProgress
    }, [isBetInProgress])

    useEffect(() => {
        spinButtonStateRef.current = spinButtonState
    }, [spinButtonState])

    useEffect(() => {
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: GameUIMessageType.CHANGE_OPACITY_HOME_BUTTON,
                    payload: {
                        isInteractive: !isHomeButtonDisabled,
                    },
                },
                '*'
            )
        }
    }, [isHomeButtonDisabled])

    // TODO: remove when promo removed from platform
    useEffect(() => {
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: GameUIMessageType.CHANGE_OPACITY_PROMO_WIDGET,
                    payload: {
                        opacity: isMenuOpen ? 0 : 1,
                        isInteractive: !isMenuOpen,
                    },
                },
                '*'
            )
        }
    }, [isMenuOpen])

    const handleFullscreenClick = () => {
        setIsFullscreen(!isFullscreen)

        const fullscreenClickedMessage: FullscreenClickedMessage = { type: GameUIMessageType.FULLSCREEN_CLICKED }

        window.postMessage(fullscreenClickedMessage)
    }
    const handleMusicClick = () => {
        if (isLongPressRef.current) return

        setIsBackgroundMusicMuted(!isBackgroundMusicMuted)

        // TODO: Delete this after new menu is tested
        if (!shouldUseExternalMenu) {
            const backgroundMusicMutedMessage: BackgroundMusicClickedMessage = {
                type: GameUIMessageType.BACKGROUND_MUSIC_CLICKED,
            }

            window.postMessage(backgroundMusicMutedMessage)
        }
    }
    const handleSoundClick = () => {
        setIsSoundMuted(!isSoundMuted)
    }
    const handleMenuClick = () => {
        if (isLongPressRef.current) return

        setIsMenuOpen(!isMenuOpen)
        playEngineSound(SOUNDS.SHOW_DENOMINATIONS)

        if (!shouldUseExternalMenu) {
            const menuClickedMessage: MenuClickedMessage = { type: GameUIMessageType.MENU_CLICKED }

            window.postMessage(menuClickedMessage)
        }
    }
    const handleHomeClick = () => {
        if (isLongPressRef.current) return

        const homeButtonClickedMessage: HomeButtonClickedMessage = { type: GameUIMessageType.HOME_BUTTON_CLICKED }

        window.postMessage(homeButtonClickedMessage)

        if (isStandalone && 'openLobby' in window && typeof window.openLobby === 'function') {
            const mgsAuthToken = (window.portalConfig as Record<string, unknown>)?.['mgs-auth-token']
            const { shouldBalanceSubtractOnly } = useConfigurationStore.getState()
            const { balance, subtractedBalance } = usePlayerInfoStore.getState()
            const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance

            window.openLobby({
                mgsAuthToken,
                balance: displayedBalance,
                baseUrl: new URL('..', document.baseURI).href.replace(/\/$/, ''),
            })
        }
    }

    const shouldHideButton =
        gameType === BaseGameType.SLOT_CONFIG_E && viewport === ViewportType.MOBILE_PORTRAIT && !isMenuOpen

    const navigationRightWrapperMobileLandscapeStyles =
        !hasBackgroundMusic &&
        viewport === ViewportType.MOBILE_LANDSCAPE &&
        !betGroup &&
        !BET_STEPPER_CONFIGS.includes(gameType)
            ? 'flex-col'
            : ''

    return (
        <>
            <div
                ref={navigationComponent}
                className={cn('absolute left-0 w-full z-[10] pointer-events-none', navigationConfigStyles)}
                style={{
                    ...navigationConfigPosition,
                }}
            >
                <div className={cn(navigationLeftWrapperConfigStyles, 'pointer-events-none')}>
                    {shouldHideHomeButton ? null : (
                        <NavButton
                            id="home-button"
                            componentName={ChangeOpacityComponents.HOME_BUTTON}
                            icon={homeIcon}
                            showBorder={false}
                            onClick={handleHomeClick}
                            disabled={isHomeButtonDisabled || isScrollingRef.current}
                        />
                    )}
                    {viewport === ViewportType.DESKTOP && !shouldHideFullScreenButton && (
                        <NavButton
                            id="fullscreen-button"
                            icon={isFullscreen ? fullscreenEnabled : fullscreenDisabled}
                            showBorder={false}
                            onClick={handleFullscreenClick}
                        />
                    )}
                </div>
                <div
                    className={cn(
                        navigationRightWrapperConfigStyles,
                        'pointer-events-none',
                        navigationRightWrapperMobileLandscapeStyles
                    )}
                >
                    {hasBackgroundMusic && (
                        <NavButton
                            id="music-button"
                            componentName={ChangeOpacityComponents.MUSIC_BUTTON}
                            icon={isBackgroundMusicMuted || isSoundMuted ? musicIconDisabled : musicIcon}
                            showBorder={false}
                            onClick={handleMusicClick}
                            disabled={isMusicButtonDisabled || isSoundMuted}
                            classNames={shouldHideButton ? 'invisible' : ''}
                        />
                    )}
                    <NavButton
                        id="sound-button"
                        componentName={ChangeOpacityComponents.SOUND_BUTTON}
                        icon={isSoundMuted ? volumeIconDisabled : volumeIcon}
                        showBorder={false}
                        onClick={handleSoundClick}
                        classNames={shouldHideButton ? 'invisible' : ''}
                    />
                    <NavButton
                        id="menu-button"
                        componentName={ChangeOpacityComponents.MENU_BUTTON}
                        icon={!isMenuOpen ? menuIcon : menuIconClosed}
                        showBorder={false}
                        onClick={handleMenuClick}
                        disabled={isMenuButtonDisabled || isLongPress}
                    />
                </div>
            </div>
            {isMenuOpen && shouldUseExternalMenu && <Menu />}
        </>
    )
}

export default Navigation
